/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useContext, useState } from 'react';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import proptypes from 'prop-types';
import { QueryContext, QueryProvider } from '@thd-nucleus/data-sources';
import {
  RecentlyViewed as RecentlyViewedComponent
} from '@thd-olt-component-react/thd-recs-containers';
import { Card, CardTitle, ThemeProvider, Typography } from '@one-thd/sui-atomic-components';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { getThemeByName } from '@thd-olt-component-react/stencil-themes';
import { HomepageContext } from '../context/HomepageContext';

const { number, string, func } = proptypes;

/**
 * We used to wrap this in a Hydrator that had a very specific id for
 * analytics purposes (rv-php-hydrator). We want to get away from this
 * kinda pattern anyway, so maybe we can spoof this by just wrapping
 * this in a `section` element or whatever the analytics expects when
 * it's looking up the tree for a nearby Hydrator.
 *
 * Update: removed Hydrator because of some weird style inconsistencies.
 * Point above still stands until we are sure we are okay with removing
 * the Hydrator permanently.
 */
export function RecentlyViewed({
  id, brandTitleMaxLine, slidesPer, hideComponentCallback, theme
}) {
  const { channel } = useContext(ExperienceContext);
  const { customerType, isB2C, defaultCardClass } = useContext(HomepageContext);
  const { defaultVariables } = useContext(QueryContext);
  const b2cPaperProps = isB2C ? { variant: 'shadow', shadow: 'none' } : {};

  const fsWorkspaceDashboardLayout = useConfigService('fs:workspaceDashboardLayout');

  const slidesToShow = (customerType !== 'b2b' || fsWorkspaceDashboardLayout?.includes('wd')) ? 5 : 2;

  const newDefaultVariables = {
    ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant
      === 'boolean' && {
      isBrandPricingPolicyCompliant: defaultVariables.current.isBrandPricingPolicyCompliant
    })
  };

  const [apiTitle, setApiTitle] = useState(null);

  return (
    <ErrorBoundary name="rv_homepage_rr">
      <QueryProvider defaultVariables={newDefaultVariables} cacheKey="rv_homepage_rr" persist>
        <ThemeProvider theme={getThemeByName(theme)}>
          <section
            id="rv-php-hydrator"
            className="sui-h-full sui-min-w-0 sui-overflow-x-auto"
          >
            <Card
              className={defaultCardClass}
              PaperProps={b2cPaperProps}
              // eslint-disable-next-line max-len
              CardContentProps={{ className: 'sui-flex sui-flex-col sui-w-full sui-h-full sui-gap-4 sui-p-4' }}
            >
              {(channel === 'mobile' || apiTitle) && (
                <CardTitle header={
                  <Typography variant="h3">{channel === 'mobile' ? 'Recently Viewed' : apiTitle}</Typography>
                }
                />
              )}
              <RecentlyViewedComponent
                brandTitleMaxLine={brandTitleMaxLine}
                // Cap slides per breakpoint to prevent layout distortion when MAA chat panel is open
                slidesToShow={{
                  sm: 2, md: 2, lg: Math.min(slidesToShow, 3), xl: Math.min(slidesToShow, 4), '2xl': slidesToShow
                }}
                hideTitle
                onTitleReady={setApiTitle}
                schemaName="rv_homepage_rr"
                showFallback
                entryId={id}
                hideComponentCallback={hideComponentCallback}
                hideATC
                hidePrice={channel === 'mobile'}
                hideCarouselArrows={channel === 'mobile'}
                theme={theme}
                enableRoundedProductImages={isB2C}
              />
            </Card>
          </section>
        </ThemeProvider>
      </QueryProvider>
    </ErrorBoundary>
  );
}
RecentlyViewed.propTypes = {
  brandTitleMaxLine: number,
  slidesPer: number,
  id: string,
  hideComponentCallback: func,
  theme: string
};

RecentlyViewed.defaultProps = {
  brandTitleMaxLine: undefined,
  slidesPer: undefined,
  id: undefined,
  hideComponentCallback: () => {},
  theme: undefined
};
